import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as _ from 'lodash';
import { fetchUserDetailsAsync, updateUserData } from './../user/userSlice';
import axiosInstance from '../../axiosInstance';
import { setShowAlert } from '../utilts/utilsSlice';
import { createResetAction, delay } from '../../utils';
import {
  AdminPauseSubscriptionCollectionParamsType,
  AdminResumeSubscriptionCollectionParamsType,
  AddPaymentMethodParamsType,
  AddSubscriptionParamsType,
  CancelSubscriptionParamsType,
  ImportCodeToPlanParamsType,
  PauseSubscriptionCollectionParamsType,
  ResumeSubscriptionCollectionParamsType,
  SubscriptionPlanType,
  SubscriptionState
} from './interfaces';
import { AccessToken } from '../user/interfaces';
import { ValidateDiscountCodeType } from '../services/subscription/types';
import { validateDiscountCode } from './../services/subscription/api';
import { ACTION_NAMES, SLICES } from '../../constants';
import { RootState, StoreMethods } from '../store';

const initialState: SubscriptionState = {
  loading: false,
  error: null,
  plans: [],
  selectedSubscription: {
    subscriptionId: '',
    creditCardRequired: null,
    tier: '',
    price: 0
  },
  latestSubscription: null
};

const subscriptions = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setSelectedSubscription: (state, action) => {
      state.selectedSubscription = action.payload;
    },
    resetSubscription: createResetAction(initialState)
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllSubscriptionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAllSubscriptionAsync.fulfilled, (state, action) => {
      state.loading = false;
      state.plans = action.payload;
    });
    builder.addCase(fetchAllSubscriptionAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(addSubscriptionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addSubscriptionAsync.fulfilled, (state, action) => {
      state.error = null;
      state.loading = false;
      state.latestSubscription = action.payload.subscriptionId;
    });
    builder.addCase(addSubscriptionAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(addPaymentMethodAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addPaymentMethodAsync.fulfilled, (state, action) => {
      state.error = null;
      state.loading = false;
    });
    builder.addCase(addPaymentMethodAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(cancelSubscriptionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(cancelSubscriptionAsync.fulfilled, (state) => {
      state.error = null;
      state.loading = false;
    });
    builder.addCase(cancelSubscriptionAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(pauseSubscriptionCollectionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(pauseSubscriptionCollectionAsync.fulfilled, (state) => {
      state.error = null;
      state.loading = false;
    });
    builder.addCase(
      pauseSubscriptionCollectionAsync.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(updatePaymentMethodAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updatePaymentMethodAsync.fulfilled, (state) => {
      state.error = null;
      state.loading = false;
    });
    builder.addCase(updatePaymentMethodAsync.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(resumeSubscriptionCollectionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(resumeSubscriptionCollectionAsync.fulfilled, (state) => {
      state.error = null;
      state.loading = false;
    });
    builder.addCase(
      resumeSubscriptionCollectionAsync.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(adminPauseSubscriptionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(adminPauseSubscriptionAsync.fulfilled, (state) => {
      state.error = null;
      state.loading = false;
    });
    builder.addCase(adminPauseSubscriptionAsync.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(adminResumeSubscriptionAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(adminResumeSubscriptionAsync.fulfilled, (state) => {
      state.error = null;
      state.loading = false;
    });
    builder.addCase(adminResumeSubscriptionAsync.rejected, (state) => {
      state.loading = false;
    });
  }
});

export const fetchAllSubscriptionAsync = createAsyncThunk<
  SubscriptionPlanType[],
  AccessToken,
  StoreMethods
>(
  'subscription/getAllSubscriptions',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const subscriptionRes = await axiosInstance.get('subscriptions/all', {
        headers: { Authorization: `Bearer ${payload?.accessToken}` }
      });
      const subscriptionData = _.get(subscriptionRes, 'data.data', []);
      return subscriptionData;
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const addSubscriptionAsync = createAsyncThunk(
  'subscription/AddSubscription',
  async (
    _payload: AddSubscriptionParamsType,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const accessToken = _.get(_payload, 'accessToken');
      const hasSubscription = _.get(_payload, 'hasSubscription');
      const userId = _.get(_payload, 'userId', '');
      const officeId = _.get(_payload, 'officeId', '');
      const payload = _.get(_payload, 'data');
      const isFree = _.get(_payload, 'isFree');
      const subscriptionRes = await axiosInstance.post(
        'user/subscribe',
        payload,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      const subscriptionData = _.get(subscriptionRes, 'data', {});
      const statusCode = _.get(subscriptionData, 'statusCode');
      const paidSubscriptionData = _.get(subscriptionData, 'data', {});
      const clientSecret = _.get(paidSubscriptionData, 'clientSecret');
      const paidSubscriptionStatus = _.get(paidSubscriptionData, 'status');
      if (statusCode === 400) {
        dispatch(
          setShowAlert({
            show: true,
            message: _.get(subscriptionData, 'msg'),
            type: 'error'
          })
        );
        return { subscriptionId: null };
      }

      // // removed codes import on 1st subscription purchase
      // if (!hasSubscription) {
      //   let retryCount = 2;
      //   let hasSuccess = false;
      //   while (!hasSuccess && retryCount > 0) {
      //     const importCodeRes = await importCodeToPlanAsync({
      //       accessToken,
      //       payload: { userId, officeId }
      //     });
      //     const statusCode = _.get(importCodeRes, 'data.statusCode');
      //     if (statusCode === 200) {
      //       hasSuccess = true;
      //     }
      //     retryCount--;
      //   }
      // }
      dispatch(
        setShowAlert({
          show: true,
          message: isFree
            ? 'Free trial activated successfully.'
            : 'Payment Successful! Subscription activated.',
          type: 'success'
        })
      );
      const subscriptionId = _.get(_payload, 'data.subscriptionId');
      await dispatch(fetchUserDetailsAsync({ accessToken }));
      return { subscriptionId, clientSecret, paidSubscriptionStatus };
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: err?.response?.data?.msg || err?.response?.data?.message,
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const addPaymentMethodAsync = createAsyncThunk(
  'subscription/AddPaymentMethod',
  async (
    _payload: AddPaymentMethodParamsType,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const { accessToken, data: payload } = _payload;
      const addPaymentMethodRes = await axiosInstance.post(
        'user/addPaymentMethod',
        { ...payload },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const addCardStatus = _.get(addPaymentMethodRes.data, 'statusCode');
      const customerId = _.get(
        addPaymentMethodRes.data,
        'data[0].customerId',
        null
      );
      if (addCardStatus === 200) {
        dispatch(
          setShowAlert({
            show: true,
            message: 'Card Details added sucessfully.',
            type: 'success'
          })
        );
      }
      return { statusCode: 200, customerId };
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: 'Invalid Card Details.',
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const cancelSubscriptionAsync = createAsyncThunk(
  'subscription/cancelSubscription',
  async (
    _payload: CancelSubscriptionParamsType,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const accessToken = _.get(_payload, 'accessToken');
      const payload = _.get(_payload, 'data');
      const cancelSubscriptionRes = await axiosInstance.post(
        'user/cancelSubscription',
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const cancelSubscriptionStatus = _.get(
        cancelSubscriptionRes.data,
        'statusCode'
      );
      await delay(2000);
      await dispatch(fetchUserDetailsAsync({ accessToken }));
      if (cancelSubscriptionStatus === 200) {
        dispatch(
          setShowAlert({
            show: true,
            message:
              'Unsubscription Successful. Thank you for being a part of our community!.',
            type: 'success'
          })
        );
      }
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: 'Cancel subscription failed.',
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const updatePaymentMethodAsync = createAsyncThunk(
  'subscription/UpdatePaymentMethod',
  async (
    _payload: AddPaymentMethodParamsType,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const { accessToken, data: payload } = _payload;
      const updatePaymentMethodRes = await axiosInstance.post(
        'user/updatePaymentMethod',
        { ...payload },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const addCardStatus = _.get(updatePaymentMethodRes.data, 'statusCode');
      if (addCardStatus === 200) {
        dispatch(
          setShowAlert({
            show: true,
            message: 'Card Details updated sucessfully.',
            type: 'success'
          })
        );
      }
      return { statusCode: 200 };
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message: 'Invalid Card Details.',
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const pauseSubscriptionCollectionAsync = createAsyncThunk(
  'subscription/pauseSubscriptionCollection',
  async (
    _payload: PauseSubscriptionCollectionParamsType,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const accessToken = _.get(_payload, 'accessToken');
      const subscriptionId = _.get(_payload, 'subscriptionId');
      const payload = _.get(_payload, 'data', {});

      const pauseSubscriptionRes = await axiosInstance.post(
        `subscriptions/${subscriptionId}/pause-collection`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const statusCode =
        _.get(pauseSubscriptionRes, 'data.statusCode') ||
        _.get(pauseSubscriptionRes, 'status');
      const message = _.get(
        pauseSubscriptionRes,
        'data.msg',
        'Subscription paused successfully.'
      );

      const pauseCollection = _.get(pauseSubscriptionRes, 'data.data.pauseCollection');
      const currentSubscription = _.get(
        getState(),
        'user.userData.subscription',
        {}
      );
      const resumesAtUnix = _.get(pauseCollection, 'resumes_at', null);
      const mappedResumeAt =
        typeof resumesAtUnix === 'number'
          ? new Date(resumesAtUnix * 1000).toISOString()
          : null;

      dispatch(
        updateUserData({
          subscription: {
            ...currentSubscription,
            pauseCollection: pauseCollection || null,
            pause: {
              ...(_.get(currentSubscription, 'pause', {}) || {}),
              isPaused: !!pauseCollection,
              resumeAt: mappedResumeAt
            }
          }
        })
      );

      await dispatch(fetchUserDetailsAsync({ accessToken }));

      if (statusCode >= 200 && statusCode < 300) {
        dispatch(
          setShowAlert({
            show: true,
            message,
            type: 'success'
          })
        );
      }

      return _.get(pauseSubscriptionRes, 'data.data', {});
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message:
              err?.response?.data?.msg ||
              err?.response?.data?.message ||
              'Pause subscription failed.',
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const resumeSubscriptionCollectionAsync = createAsyncThunk(
  'subscription/resumeSubscriptionCollection',
  async (
    _payload: ResumeSubscriptionCollectionParamsType,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const accessToken = _.get(_payload, 'accessToken');
      const subscriptionId = _.get(_payload, 'subscriptionId');
      const payload = _.get(_payload, 'data', {});

      const resumeSubscriptionRes = await axiosInstance.post(
        `subscriptions/${subscriptionId}/resume-collection`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const statusCode =
        _.get(resumeSubscriptionRes, 'data.statusCode') ||
        _.get(resumeSubscriptionRes, 'status');
      const message = _.get(
        resumeSubscriptionRes,
        'data.msg',
        'Subscription payment collection resumed successfully'
      );

      const currentSubscription = _.get(
        getState(),
        'user.userData.subscription',
        {}
      );

      dispatch(
        updateUserData({
          subscription: {
            ...currentSubscription,
            pauseCollection: null,
            pause: {
              ...(_.get(currentSubscription, 'pause', {}) || {}),
              isPaused: false,
              resumeAt: null,
              pausedAt: null
            }
          }
        })
      );

      await dispatch(fetchUserDetailsAsync({ accessToken }));

      if (statusCode >= 200 && statusCode < 300) {
        dispatch(
          setShowAlert({
            show: true,
            message,
            type: 'success'
          })
        );
      }

      return _.get(resumeSubscriptionRes, 'data.data', {});
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message:
              err?.response?.data?.msg ||
              err?.response?.data?.message ||
              'Resume subscription failed.',
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const adminPauseSubscriptionAsync = createAsyncThunk(
  'subscription/adminPauseSubscriptionCollection',
  async (
    _payload: AdminPauseSubscriptionCollectionParamsType,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const accessToken = _.get(_payload, 'accessToken');
      const subscriptionId = _.get(_payload, 'subscriptionId');
      const payload = _.get(_payload, 'data', {});

      const pauseSubscriptionRes = await axiosInstance.post(
        `subscriptions/${subscriptionId}/pause-collection`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const statusCode =
        _.get(pauseSubscriptionRes, 'data.statusCode') ||
        _.get(pauseSubscriptionRes, 'status');
      const message = _.get(
        pauseSubscriptionRes,
        'data.msg',
        'Subscription paused successfully.'
      );

      if (statusCode >= 200 && statusCode < 300) {
        dispatch(
          setShowAlert({
            show: true,
            message,
            type: 'success'
          })
        );
      }

      return _.get(pauseSubscriptionRes, 'data.data', {});
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message:
              err?.response?.data?.msg ||
              err?.response?.data?.message ||
              'Pause subscription failed.',
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const adminResumeSubscriptionAsync = createAsyncThunk(
  'subscription/adminResumeSubscriptionCollection',
  async (
    _payload: AdminResumeSubscriptionCollectionParamsType,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const accessToken = _.get(_payload, 'accessToken');
      const subscriptionId = _.get(_payload, 'subscriptionId');
      const payload = _.get(_payload, 'data', {});

      const resumeSubscriptionRes = await axiosInstance.post(
        `subscriptions/${subscriptionId}/resume-collection`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const statusCode =
        _.get(resumeSubscriptionRes, 'data.statusCode') ||
        _.get(resumeSubscriptionRes, 'status');
      const message = _.get(
        resumeSubscriptionRes,
        'data.msg',
        'Subscription payment collection resumed successfully'
      );

      if (statusCode >= 200 && statusCode < 300) {
        dispatch(
          setShowAlert({
            show: true,
            message,
            type: 'success'
          })
        );
      }

      return _.get(resumeSubscriptionRes, 'data.data', {});
    } catch (err) {
      if (err?.response?.data.statusCode !== 401) {
        dispatch(
          setShowAlert({
            show: true,
            message:
              err?.response?.data?.msg ||
              err?.response?.data?.message ||
              'Resume subscription failed.',
            type: 'error'
          })
        );
      }
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const importCodeToPlanAsync = async (
  _payload: ImportCodeToPlanParamsType
) => {
  try {
    const accessToken = _.get(_payload, 'accessToken', '');
    const payload = _.get(_payload, 'payload', {});
    const importCodeRes = await axiosInstance.post(
      'procedure-codes-import',
      { ...payload },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    return importCodeRes;
  } catch (err) {
    console.log('Import codes and plans failed:', err);
    return err?.response?.data;
  }
};

export const validateDiscountCodeAsync = createAsyncThunk(
  `${SLICES.SUBSCRIPTION}/${ACTION_NAMES.VALIDATE_DISCOUNT_CODE}`,
  async (payload: ValidateDiscountCodeType, { rejectWithValue }) => {
    try {
      const res = await validateDiscountCode(payload);
      return res;
    } catch (err) {
      console.log('Error', err);
    }
  }
);

export const { setSelectedSubscription, resetSubscription } =
  subscriptions.actions;

export default subscriptions.reducer;

export const getSelectedSubscription = (state: RootState) =>
  state.subscription.selectedSubscription;
